import { apiClient } from '@/lib/api/client'

export interface TwoFactorStatus {
  isEnabled: boolean
  isRequired: boolean
  hasBackupCodes: boolean
  lastUsedAt?: string
  system_enabled?: boolean
  system_required?: boolean
  can_disable?: boolean
  enabled?: boolean
  backup_codes_count?: number
  last_used?: string
}

export class TwoFactorService {
  static async isRequired(email: string): Promise<boolean> {
    try {
      // Backend doesn't have this endpoint, but we can check via login response
      // This method is kept for compatibility but may not be used
      return false
    } catch (error) {
      console.error('Error checking 2FA requirement:', error)
      return false
    }
  }

  static async sendCode(email: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post('/generate-code', { email })
      return {
        success: true,
        message: response.data.message || 'Verification code sent successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.errors?.[0] || error.response?.data?.message || 'Failed to send verification code'
      }
    }
  }

  static async verifyCode(email: string, code: string, rememberMe?: boolean): Promise<{ success: boolean; user?: any; token?: string; message?: string }> {
    try {
      // Send application parameter to filter navigation for Auth application
      const response = await apiClient.post('/verify-code', {
        email,
        code,
        remember_me: rememberMe ?? false,
        application: 'auth', // Filter navigation by Auth application
      })
      
      // Backend returns { user, token, message } on success
      if (response.data.user && response.data.token) {
        return {
          success: true,
          user: response.data.user,
          token: response.data.token,
          message: response.data.message || 'Verification successful'
        }
      }
      
      return {
        success: false,
        message: response.data.message || 'Verification failed'
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.errors?.[0] || error.response?.data?.message || 'Invalid verification code'
      }
    }
  }

  static async getStatus(): Promise<TwoFactorStatus> {
    try {
      const response = await apiClient.get('/2fa/status')
      const twoFactor = response.data.two_factor || {}
      return {
        isEnabled: twoFactor.enabled || twoFactor.is_enabled || false,
        isRequired: twoFactor.system_required || false,
        hasBackupCodes: (twoFactor.backup_codes_count || 0) > 0 || (twoFactor.backup_codes?.length || 0) > 0,
        lastUsedAt: twoFactor.last_used || twoFactor.last_used_at,
        system_enabled: twoFactor.system_enabled || false,
        system_required: twoFactor.system_required || false,
        can_disable: twoFactor.can_disable !== false,
        enabled: twoFactor.enabled || false,
        backup_codes_count: twoFactor.backup_codes_count || 0,
        last_used: twoFactor.last_used || twoFactor.last_used_at
      }
    } catch (error) {
      console.error('Error getting 2FA status:', error)
      return {
        isEnabled: false,
        isRequired: false,
        hasBackupCodes: false,
        system_enabled: false,
        system_required: false,
        can_disable: true
      }
    }
  }

  static async enable(): Promise<{ success: boolean; message?: string; backupCodes?: string[] }> {
    try {
      const response = await apiClient.post('/2fa/enable')
      return {
        success: response.data.success || false,
        message: response.data.message,
        backupCodes: response.data.backup_codes
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to enable 2FA'
      }
    }
  }

  static async disable(): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post('/2fa/disable')
      return {
        success: response.data.success || false,
        message: response.data.message
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to disable 2FA'
      }
    }
  }

  static async generateBackupCodes(): Promise<{ success: boolean; backupCodes?: string[]; message?: string }> {
    try {
      const response = await apiClient.post('/2fa/generate-backup-codes')
      return {
        success: response.data.success || false,
        backupCodes: response.data.backup_codes,
        message: response.data.message
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to generate backup codes'
      }
    }
  }
}

